import { motion, useReducedMotion } from 'motion/react';
import { useMemo, useState } from 'react';

import type { CalendarWeekColumn } from '~/types/earningsCalendar.types';
import { MOBILE_HEADER_STICKY_TOP_CLASS } from '~/ui-apps/design-system/constants/mobileSignedInHeader';

import DataUnavailableBanner from './components/DataUnavailableBanner';
import DaySection from './components/DaySection';
import EarningsSearchInput from './components/EarningsSearchInput';
import EarningsSearchResults from './components/EarningsSearchResults';
import WeekPills from './components/WeekPills';
import {
  HERO_AHA_EASE,
  HEADER_REVEAL,
  PAGE_REVEAL_EASE,
} from './constants/pageReveal.constants';
import { weekTabLabel } from './constants/weekTabs.constants';
import { flattenCalendarForSearch, searchEarningsHits } from './utils/fuzzyMatch';

export type EarningsCalendarAppProps = {
  columns: CalendarWeekColumn[];
  dataUnavailable: boolean;
  todayWeekIndex: number | null;
  todayRowIndex: number | null;
};

export default function EarningsCalendarApp({
  columns,
  dataUnavailable,
  todayWeekIndex,
  todayRowIndex,
}: EarningsCalendarAppProps) {
  const reduceMotion = useReducedMotion();
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedColumn = columns[selectedWeekIndex];
  const isCurrentWeek = selectedWeekIndex === 1;

  const searchIndex = useMemo(() => flattenCalendarForSearch(columns), [columns]);
  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;
  const searchHits = useMemo(
    () => (isSearching ? searchEarningsHits(searchIndex, trimmedQuery) : []),
    [isSearching, searchIndex, trimmedQuery],
  );

  return (
    <div className="w-full bg-background">
      <div className="mx-auto mt-16 flex w-full min-w-0 max-w-2xl flex-col justify-start px-3 pb-10 pt-4 sm:px-6 md:px-8 md:pb-14 md:pt-6">
        <div className="flex w-full min-w-0 flex-col gap-4 md:gap-6">
          <motion.header
            className="flex flex-col gap-5 md:gap-6"
            initial={
              reduceMotion
                ? false
                : { opacity: 0, y: 14, filter: 'blur(6px)' }
            }
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{
              duration: reduceMotion ? 0.01 : HEADER_REVEAL.duration,
              delay: reduceMotion ? 0 : HEADER_REVEAL.delay,
              ease: HERO_AHA_EASE,
            }}
          >
            <h1 className="text-center text-2xl font-black tracking-[-0.03em] text-slate-900 md:text-3xl">
              Earnings Calendar
            </h1>
            <EarningsSearchInput value={searchQuery} onChange={setSearchQuery} />
          </motion.header>

          {dataUnavailable ? (
            <motion.div
              className="w-full"
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: reduceMotion ? 0.01 : 0.4,
                delay: reduceMotion ? 0 : 0.35,
                ease: PAGE_REVEAL_EASE,
              }}
            >
              <DataUnavailableBanner />
            </motion.div>
          ) : null}

          {isSearching ? (
            <EarningsSearchResults
              query={trimmedQuery}
              hits={searchHits}
              todayWeekIndex={todayWeekIndex}
              todayRowIndex={todayRowIndex}
            />
          ) : (
            <div className="flex w-full min-w-0 flex-col gap-4 md:gap-5">
              <div
                className={`sticky top-0 z-20 -mx-3 bg-background px-3 py-2 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8 ${MOBILE_HEADER_STICKY_TOP_CLASS}`}
              >
                <WeekPills
                  selectedIndex={selectedWeekIndex}
                  onSelect={setSelectedWeekIndex}
                />
              </div>

              <div
                className="flex w-full min-w-0 flex-col gap-3.5 md:gap-4"
                role="region"
                aria-label={`Earnings calendar — ${weekTabLabel(selectedWeekIndex)}`}
                key={selectedWeekIndex}
              >
                {selectedColumn.days.map((day, rowIndex) => {
                  const items = selectedColumn.cells[rowIndex] ?? [];
                  const isToday =
                    todayWeekIndex === selectedWeekIndex && todayRowIndex === rowIndex;

                  return (
                    <DaySection
                      key={`${selectedWeekIndex}-${day.date}-${rowIndex}`}
                      day={day}
                      items={items}
                      sequence={rowIndex}
                      isToday={isToday}
                      isCurrentWeek={isCurrentWeek}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
